import { useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { Announcement } from '@interfaces/general';
import { AnnouncementOptionButtons } from '../shared/AnnouncementOptionButtons';
import { useRespondAnnouncement } from '../CommunicationsModal/hooks/useRespondAnnouncement';

interface Props {
  announcement: Announcement;
  onClose: () => void;
}

export const CommunicationsBanner = ({ announcement, onClose }: Props) => {
  // Instancia local del hook → isSubmitting solo afecta a este banner.
  const { respond, isSubmitting } = useRespondAnnouncement();

  const bgColor = announcement.bg_color || '#1A1A2E';
  const textColor = announcement.text_color || '#FFFFFF';

  // delay_before_close (segundos): habilita la X tras N segundos
  const initialDelay = announcement.delay_before_close ?? 0;
  const [canClose, setCanClose] = useState(initialDelay === 0);

  useEffect(() => {
    if (initialDelay > 0) {
      const t = setTimeout(() => setCanClose(true), initialDelay * 1000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [initialDelay]);

  // Persistente: la X solo aparece si ya respondió (porque ya hay user_response)
  const isPersistent = announcement.is_persistent === true;
  const showCloseButton = !isPersistent && canClose;

  return (
    <div
      role="region"
      aria-label={announcement.title}
      style={{ backgroundColor: bgColor, color: textColor }}
      className="w-full px-4 py-2.5 flex items-center gap-3 shadow-sm"
    >
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{announcement.title}</p>
          {(announcement.subtitle || announcement.text) && (
            <p className="text-xs opacity-90 truncate">{announcement.subtitle || announcement.text}</p>
          )}
        </div>

        {announcement.options && announcement.options.length > 0 && (
          <div className="mt-1 sm:mt-0 sm:ml-auto">
            <AnnouncementOptionButtons
              options={announcement.options}
              announcementId={announcement.uuid}
              userResponse={announcement.user_response}
              bgColor={bgColor}
              textColor={textColor}
              isSubmitting={isSubmitting}
              variant="banner"
              respond={respond}
              onComplete={onClose}
            />
          </div>
        )}
      </div>

      {showCloseButton && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar anuncio"
          className="flex-shrink-0 p-1 rounded hover:opacity-80 transition-opacity"
          style={{ color: textColor }}
        >
          <IconX size={18} />
        </button>
      )}
    </div>
  );
};
